// Inside-the-Canvas half of the camera "fly to" (see flight.ts): binds a
// CameraFlightController to the scene's OrbitControls + camera and steps it
// every frame. Scene mounts it next to the CinematicCameraRig; both drive the
// same controls ref, and the flight backs off whenever the rig has disabled
// the controls for a cinematic.

import { useFrame, useThree } from '@react-three/fiber';
import { type RefObject, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import {
  CameraFlightController,
  type CameraFocusRequest,
  type FlightControls,
  isNearScreenEdge,
} from './flight.js';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

export function CameraFlight({
  controls,
  request,
}: {
  readonly controls: RefObject<FlightControls | null>;
  readonly request: CameraFocusRequest | null | undefined;
}) {
  const camera = useThree((s) => s.camera);
  const controller = useMemo(() => {
    const ndc = new THREE.Vector3();
    return new CameraFlightController({
      now: () => performance.now(),
      prefersReducedMotion,
      isNearEdge: (p) => isNearScreenEdge(ndc.set(p[0], p[1], p[2]).project(camera)),
    });
  }, [camera]);

  useEffect(() => {
    const ctl = controls.current;
    if (!ctl) return;
    return controller.attach(ctl, camera);
  }, [controller, controls, camera]);

  // Priority 0: after drei's OrbitControls.update() (priority -1), so this
  // frame renders the flight's pose and the next update() picks it up as
  // the new orbit centre. The controller ignores a seq it has seen.
  useFrame(() => {
    if (request) controller.request(request);
    controller.frame();
  });

  return null;
}
